
import React, { useState, useEffect } from 'react';
import { BuybackDeviceType, DeviceModelInfo, BuybackPricingFactors, Employee, BuybackCondition, BuybackAccessory } from '../types';
import { Button } from './Button';
import { BUYBACK_DEVICE_TYPES, BUYBACK_CONDITIONS, BUYBACK_ACCESSORIES_LIST } from '../constants';

interface AdminBuybackSettingsPageProps {
  buybackDeviceModels: Record<BuybackDeviceType, DeviceModelInfo[]>;
  buybackPricingFactors: BuybackPricingFactors;
  onSaveBuybackSettings: (
    updatedModels: Record<BuybackDeviceType, DeviceModelInfo[]>,
    updatedFactors: BuybackPricingFactors
  ) => void;
  currentUser: Employee | null;
}

const WrenchScrewdriverIconSolid: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 mr-2 text-[var(--color-primary)]" {...props}>
    <path d="M12 2.25a.75.75 0 01.75.75v2.444a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM3.276 5.983a.75.75 0 011.06 0l1.733 1.732a.75.75 0 11-1.06 1.061L3.276 7.044a.75.75 0 010-1.06zM20.724 5.983a.75.75 0 010 1.06l-1.732 1.732a.75.75 0 11-1.061-1.06L19.664 5.983a.75.75 0 011.06 0z" />
    <path fillRule="evenodd" d="M12 4.5a.75.75 0 01.75.75v3a.75.75 0 01-1.5 0v-3A.75.75 0 0112 4.5zM5.43 8.657a.75.75 0 011.061 0L9 11.168V15a.75.75 0 01-1.5 0v-3.082l-2.151-2.151a.75.75 0 010-1.11zM18.57 8.657a.75.75 0 010 1.11L16.5 11.918V15a.75.75 0 01-1.5 0v-3.082l2.151-2.151a.75.75 0 011.06-.001zM7.155 17.823a.75.75 0 01.937-.125 6 6 0 007.816 0 .75.75 0 01.937.125l1.407.844a.75.75 0 01.108.938 7.5 7.5 0 01-11.182 0 .75.75 0 01.108-.938l1.407-.844z" clipRule="evenodd" />
    <path d="M12 18.75a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-1.5a.75.75 0 01.75-.75z" />
  </svg>
);


export const AdminBuybackSettingsPage: React.FC<AdminBuybackSettingsPageProps> = ({
  buybackDeviceModels: initialModels,
  buybackPricingFactors: initialFactors,
  onSaveBuybackSettings,
  currentUser,
}) => {
  const [editableModels, setEditableModels] = useState<Record<BuybackDeviceType, DeviceModelInfo[]>>(() => JSON.parse(JSON.stringify(initialModels)));
  const [editableFactors, setEditableFactors] = useState<BuybackPricingFactors>(() => JSON.parse(JSON.stringify(initialFactors)));
  const [newModelForms, setNewModelForms] = useState<Partial<Record<BuybackDeviceType, DeviceModelInfo>>>({});

  useEffect(() => {
    setEditableModels(JSON.parse(JSON.stringify(initialModels)));
  }, [initialModels]);

  useEffect(() => {
    setEditableFactors(JSON.parse(JSON.stringify(initialFactors)));
  }, [initialFactors]);

  const handleModelChange = (
    deviceType: BuybackDeviceType,
    modelId: string,
    field: keyof DeviceModelInfo,
    value: string | number | string[]
  ) => {
    setEditableModels((prev: Record<BuybackDeviceType, DeviceModelInfo[]>) => ({
      ...prev,
      [deviceType]: prev[deviceType].map(model =>
        model.id === modelId ? { ...model, [field]: value } : model
      ),
    }));
  };
  
  const handleStorageOptionsChange = (deviceType: BuybackDeviceType, modelId: string, value: string) => {
     handleModelChange(deviceType, modelId, 'storageOptions', value.split(',').map(s => s.trim()).filter(s => s));
  };

  const handleAddNewModelChange = (deviceType: BuybackDeviceType, field: keyof Omit<DeviceModelInfo, 'id'>, value: string) => {
    setNewModelForms(prev => ({
        ...prev,
        [deviceType]: {
            ...(prev[deviceType] || { name: '', baseValue: 0, storageOptions: [] }),
            [field]: field === 'baseValue' ? parseFloat(value) || 0 : value,
        }
    }));
  };
  
  const handleAddNewModelStorageOptionsChange = (deviceType: BuybackDeviceType, value: string) => {
    setNewModelForms(prev => ({
        ...prev,
        [deviceType]: {
            ...(prev[deviceType] || { name: '', baseValue: 0 }),
            storageOptions: value.split(',').map(s => s.trim()).filter(s => s),
        }
    }));
  };

  const handleAddModel = (deviceType: BuybackDeviceType) => {
    const newModelData = newModelForms[deviceType];
    if (!newModelData || !newModelData.name || typeof newModelData.baseValue !== 'number') {
        alert("New model must have a name and a valid base value.");
        return;
    }
    const newModel: DeviceModelInfo = {
        id: `${newModelData.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`,
        name: newModelData.name,
        baseValue: newModelData.baseValue,
        storageOptions: newModelData.storageOptions || [],
    };
    setEditableModels(prev => ({
        ...prev,
        [deviceType]: [...(prev[deviceType] || []), newModel]
    }));
    setNewModelForms(prev => ({ ...prev, [deviceType]: { name: '', baseValue: 0, storageOptions: [] } }));
  };

  const handleDeleteModel = (deviceType: BuybackDeviceType, modelId: string) => {
    if (window.confirm(`Are you sure you want to delete model ${modelId} for ${deviceType}?`)) {
        setEditableModels(prev => ({
            ...prev,
            [deviceType]: prev[deviceType].filter(model => model.id !== modelId)
        }));
    }
  };
  
  const handleFactorChange = (
    factorType: keyof BuybackPricingFactors, 
    key: string, // BuybackCondition, storage string, or BuybackAccessory
    value: string
  ) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    setEditableFactors((prev: BuybackPricingFactors) => {
        const newFactors = { ...prev };
        if (factorType === 'conditionMultiplier' || factorType === 'accessoryValue' || factorType === 'storageBonusPercentage') {
            (newFactors[factorType] as Record<string, number>)[key] = numValue;
        } else if (factorType === 'MINIMUM_POOR_VALUE') {
             (newFactors[factorType] as unknown as number) = numValue; // Type assertion to satisfy compiler for specific key
        }
        return newFactors;
    });
  };


  const handleSave = () => {
    onSaveBuybackSettings(editableModels, editableFactors);
  };

  const inputClass = "form-input text-sm";
  const labelClass = "block text-xs font-medium text-slate-600 mb-0.5";
  const sectionClass = "bg-white p-6 rounded-lg shadow-md";
  const tableClass = "min-w-full divide-y divide-slate-200 text-sm";
  const thClass = "px-4 py-2 text-left font-medium text-slate-500";
  const tdClass = "px-4 py-2 whitespace-nowrap";

  return (
    <div className="space-y-6">
      <div className={sectionClass}>
        <div className="flex items-center mb-1">
          <WrenchScrewdriverIconSolid />
          <h1 className="text-2xl font-bold text-[var(--color-primary)]">Admin: Buyback Program Settings</h1>
        </div>
        <p className="text-sm text-slate-600">Manage device models and pricing for the buyback program.</p>
      </div>

      {BUYBACK_DEVICE_TYPES.map(deviceType => (
        <div key={deviceType} className={sectionClass}>
          <h2 className="text-xl font-semibold text-slate-700 mb-4 border-b border-slate-200 pb-2">{deviceType} Models</h2>
          <div className="overflow-x-auto mb-6">
            <table className={tableClass}>
              <thead className="bg-slate-50">
                <tr>
                  <th className={thClass}>Name</th>
                  <th className={thClass}>Base Value</th>
                  <th className={thClass}>Storage Options (CSV)</th>
                  <th className={thClass}>Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {editableModels[deviceType]?.map(model => (
                  <tr key={model.id}>
                    <td className={tdClass}><input type="text" value={model.name} onChange={(e) => handleModelChange(deviceType, model.id, 'name', e.target.value)} className={inputClass} /></td>
                    <td className={tdClass}><input type="number" value={model.baseValue} onChange={(e) => handleModelChange(deviceType, model.id, 'baseValue', parseFloat(e.target.value))} className={`${inputClass} w-24`} step="0.01" /></td>
                    <td className={tdClass}><input type="text" value={(model.storageOptions || []).join(', ')} onChange={(e) => handleStorageOptionsChange(deviceType, model.id, e.target.value)} className={inputClass} placeholder="e.g. 128GB,256GB,512GB" /></td>
                    <td className={tdClass}>
                      <Button variant="danger" onClick={() => handleDeleteModel(deviceType, model.id)} className="text-xs !px-2 !py-1">Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-200 pt-4">
            <h3 className="text-md font-semibold text-slate-600 mb-2">Add New {deviceType} Model</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <input type="text" placeholder="Model Name" value={newModelForms[deviceType]?.name || ''} onChange={(e) => handleAddNewModelChange(deviceType, 'name', e.target.value)} className={inputClass} />
                <input type="number" placeholder="Base Value" value={newModelForms[deviceType]?.baseValue ?? ''} onChange={(e) => handleAddNewModelChange(deviceType, 'baseValue', e.target.value)} className={inputClass} step="0.01"/>
                <input type="text" placeholder="Storage (CSV)" value={(newModelForms[deviceType]?.storageOptions || []).join(', ')} onChange={(e) => handleAddNewModelStorageOptionsChange(deviceType, e.target.value)} className={inputClass} />
            </div>
            <Button variant="secondary" onClick={() => handleAddModel(deviceType)} className="mt-2 text-xs !px-3 !py-1.5">Add Model</Button>
          </div>
        </div>
      ))}
      
      <div className={sectionClass}>
        <h2 className="text-xl font-semibold text-slate-700 mb-4 border-b border-slate-200 pb-2">Pricing Factors</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
                <h3 className="text-md font-semibold text-slate-600 mb-2">Condition Multipliers</h3>
                {BUYBACK_CONDITIONS.map(condition => (
                    <div key={condition} className="mb-2 flex items-center">
                        <label htmlFor={`factor-cond-${condition}`} className={`${labelClass} w-1/2`}>{condition}:</label>
                        <input type="number" id={`factor-cond-${condition}`} value={editableFactors.conditionMultiplier[condition]} onChange={(e) => handleFactorChange('conditionMultiplier', condition, e.target.value)} className={`${inputClass} w-1/2`} step="0.01" />
                    </div>
                ))}
            </div>
            <div>
                <h3 className="text-md font-semibold text-slate-600 mb-2">Accessory Values</h3>
                {BUYBACK_ACCESSORIES_LIST.map(accessory => (
                     <div key={accessory} className="mb-2 flex items-center">
                        <label htmlFor={`factor-acc-${accessory}`} className={`${labelClass} w-1/2`}>{accessory}:</label>
                        <input type="number" id={`factor-acc-${accessory}`} value={editableFactors.accessoryValue[accessory]} onChange={(e) => handleFactorChange('accessoryValue', accessory, e.target.value)} className={`${inputClass} w-1/2`} step="0.01" />
                    </div>
                ))}
            </div>
             <div className="md:col-span-2">
                <h3 className="text-md font-semibold text-slate-600 mb-2">Storage Bonus Percentages (relative to base value)</h3>
                {Object.entries(editableFactors.storageBonusPercentage).map(([storageKey, value]) => (
                    <div key={storageKey} className="mb-2 flex items-center">
                        <label htmlFor={`factor-storage-${storageKey}`} className={`${labelClass} w-1/2`}>{storageKey}:</label>
                        <input type="number" id={`factor-storage-${storageKey}`} value={value} onChange={(e) => handleFactorChange('storageBonusPercentage', storageKey, e.target.value)} className={`${inputClass} w-1/2`} step="0.01" placeholder="e.g. 0.05 for 5%" />
                    </div>
                ))}
            </div>
            <div>
                <h3 className="text-md font-semibold text-slate-600 mb-2">Minimum Poor Value</h3>
                 <div className="mb-2 flex items-center">
                    <label htmlFor="factor-min-poor" className={`${labelClass} w-1/2`}>Min Value ($):</label>
                    <input type="number" id="factor-min-poor" value={editableFactors.MINIMUM_POOR_VALUE} onChange={(e) => setEditableFactors(prev => ({...prev, MINIMUM_POOR_VALUE: parseFloat(e.target.value) || 0}))} className={`${inputClass} w-1/2`} step="0.01" />
                </div>
            </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button variant="primary" onClick={handleSave} className="text-base px-6 py-2.5">
          Save All Buyback Settings
        </Button>
      </div>
    </div>
  );
};
